import { useState } from 'react'
import { Chip, Table } from '@heroui/react'

import { ModuleIntro } from '@/components/layout'
import { EmptyState, ErrorState, LoadingState, Pagination } from '@/components/ui/data-state'
import { FilterSelect } from '@/components/ui/filter-select'

import { useInventoryLocationList, useInventoryLocations } from '../hooks'
import type { InventoryLocation, InventoryLocationFilterValues } from '../types'
import { locationStatusLabels, locationTypeLabels } from './inventory-format'

const PAGE_SIZE = 25

interface LocationFiltersProps {
  value: InventoryLocationFilterValues
  onChange: (value: InventoryLocationFilterValues) => void
}

/** Type and status, for the module bar — two controls fit beside the tabs. */
export function LocationFilters({ value, onChange }: LocationFiltersProps) {
  return (
    <span className="flex flex-wrap items-center gap-3">
      <FilterSelect
        label="Type"
        allLabel="All types"
        value={value.type}
        options={(Object.keys(locationTypeLabels) as InventoryLocation['type'][]).map((type) => ({
          value: type,
          label: locationTypeLabels[type],
        }))}
        onChange={(type) => onChange({ ...value, type: type as InventoryLocation['type'] | null })}
      />
      <FilterSelect
        label="Status"
        allLabel="All statuses"
        value={value.status}
        options={(Object.keys(locationStatusLabels) as InventoryLocation['status'][]).map(
          (status) => ({ value: status, label: locationStatusLabels[status] }),
        )}
        onChange={(status) =>
          onChange({ ...value, status: status as InventoryLocation['status'] | null })
        }
      />
    </span>
  )
}

interface LocationsPanelProps {
  filters: InventoryLocationFilterValues
  onClearFilters: () => void
}

/**
 * Where stock is held: the branches, and the warehouses that supply them.
 *
 * Read-only, and the intro says why: these are mirrored from the business's own
 * branches, so a branch opened or closed under Business shows up here by itself.
 * There is nothing to create on this side — a location made here would be one
 * the rest of the system had never heard of.
 */
export function LocationsPanel({ filters, onClearFilters }: LocationsPanelProps) {
  const [page, setPage] = useState(0)
  /** The page belongs to the filters it was turned under; new filters start on the first. */
  const [pageOf, setPageOf] = useState(filters)

  if (pageOf !== filters) {
    setPageOf(filters)
    setPage(0)
  }

  const { locations, total, isLoading, isRefreshing, error, retry } = useInventoryLocationList(
    filters,
    page,
    PAGE_SIZE,
  )
  /* The active list, for naming the branch a warehouse belongs to — that branch
     may be on another page of this one. */
  const { locations: active } = useInventoryLocations()

  const isFiltered = Boolean(filters.type || filters.status)

  return (
    <>
      <ModuleIntro description="Your branches, and the warehouses that supply them. They come from your branches under Business, so opening or closing one there updates this list." />

      {isLoading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState subject="your locations" error={error} onRetry={retry} />
      ) : locations.length === 0 ? (
        <EmptyState
          title={isFiltered ? 'Nothing matches these filters.' : 'No locations yet.'}
          description={
            isFiltered
              ? 'Clear the filters to see every location.'
              : 'Locations appear once your branches reach the inventory service. If you have branches under Business and none show here, they have not been sent across yet.'
          }
          action={isFiltered ? { label: 'Clear filters', onPress: onClearFilters } : undefined}
        />
      ) : (
        <>
          <div className={isRefreshing ? 'opacity-60 transition-opacity' : undefined}>
            <Table>
              <Table.ScrollContainer>
                <Table.Content aria-label="Locations">
                  <Table.Header>
                    <Table.Column id="name" isRowHeader>
                      Location
                    </Table.Column>
                    <Table.Column id="type">Type</Table.Column>
                    <Table.Column id="branch">Supplies</Table.Column>
                    <Table.Column id="status">Status</Table.Column>
                  </Table.Header>

                  <Table.Body>
                    {locations.map((location) => (
                      <Table.Row key={location.id} id={location.id}>
                        <Table.Cell>
                          <span className="font-medium">{location.name}</span>
                        </Table.Cell>
                        <Table.Cell>
                          <span className="text-muted">{locationTypeLabels[location.type]}</span>
                        </Table.Cell>
                        <Table.Cell>
                          <span className="text-muted">{suppliedBranch(location, active)}</span>
                        </Table.Cell>
                        <Table.Cell>
                          <Chip
                            size="sm"
                            variant="soft"
                            color={location.status === 'active' ? 'success' : 'default'}
                          >
                            {locationStatusLabels[location.status]}
                          </Chip>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Content>
              </Table.ScrollContainer>
            </Table>
          </div>

          <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
        </>
      )}
    </>
  )
}

/**
 * Which outlet a location serves: itself, for a branch; its owning branch, for a
 * warehouse; and "Every branch" for a central warehouse that belongs to none.
 */
function suppliedBranch(location: InventoryLocation, active: readonly InventoryLocation[]): string {
  if (location.type === 'branch') return 'Itself'
  if (!location.branchId) return 'Every branch (central)'

  const branch = active.find(
    (candidate) => candidate.type === 'branch' && candidate.branchId === location.branchId,
  )

  // Not in the active list: closed, or past its first page. Neither is worth guessing at.
  return branch?.name ?? '—'
}
